/**
 * Guarded payload construction for PERSISTED doctor room moves / swaps / transfers.
 *
 * WHY THIS EXISTS: previously a "swap" or "transfer" only wrote a browser-local room-transfer OVERLAY
 * (roomTransfers.ts). The underlying allocation kept the OLD doctor, so any view reading raw
 * `allocatedDoctorIds` (e.g. the Allocated-Shifts scorecard) still showed the doctor on the rooms they were
 * moved off — the "stale names / phantom >4 rooms" bug. The runner asked for the move to be REAL: rewrite the
 * allocation so every view agrees, with an Undo to reverse it.
 *
 * Like recolor.ts, this is a SAFETY-critical pure module: `PUT /allocations` is delete-and-rewrite (it deletes
 * every allocation in the window and recreates only the request body — backend/src/update-allocations.ts), so
 * a short/empty payload DELETES the day. Deciding "may we write, and with what?" lives here, tested, so no
 * caller can reintroduce the bug. One generic `buildDoctorEditPayload` powers moves, swaps AND undo (undo just
 * feeds the captured prior doctor ids back in).
 */

/** Minimal allocation shape; both callers' types structurally satisfy it. */
export interface MovableAllocation {
  operatingRoomScheduleId: string;
  allocatedNurseIds?: string[];
  allocatedDoctorIds?: string[];
  colorOverrides?: Record<string, string>;
}

export interface MovePayloadEntry {
  scheduleId: string;
  allocatedNurseIds: string[];
  allocatedDoctorIds: string[];
  colorOverrides: Record<string, string>;
}

/** One edit: set THIS schedule's doctor list to exactly `nextDoctorIds`. */
export interface DoctorEdit { scheduleId: string; nextDoctorIds: string[]; }

/** The prior state of a touched schedule — pushed onto the Undo stack so a click restores it. */
export interface DoctorUndoChange { scheduleId: string; priorDoctorIds: string[]; }

export interface DoctorMoveResult {
  /** Allocations with the edits applied — for optimistic local state. */
  nextAllocations: MovableAllocation[];
  /** The full-window body to PUT. */
  payload: MovePayloadEntry[];
  /** Inverse of the edits (prior doctor ids of every schedule that actually changed) — the Undo entry. */
  undo: DoctorUndoChange[];
}

const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * Apply `edits` (each sets one schedule's `allocatedDoctorIds`) and return the FULL-window payload to persist,
 * plus the Undo inverse.
 *
 * Returns `null` — "do not write at all" — when writing would be unsafe or a no-op:
 *   - `allocationsLoaded` is false (the list may be empty only because the fetch failed → writing erases the day);
 *   - the list is empty (an empty body is a full-window delete);
 *   - no edit actually changes anything (pure risk for zero benefit).
 */
export function buildDoctorEditPayload(
  allocations: MovableAllocation[],
  edits: DoctorEdit[],
  allocationsLoaded: boolean,
): DoctorMoveResult | null {
  if (!allocationsLoaded) return null;
  if (!allocations.length) return null;

  const editBySchedule = new Map<string, string[]>();
  for (const e of edits) editBySchedule.set(e.scheduleId, e.nextDoctorIds);

  const undo: DoctorUndoChange[] = [];
  let touched = false;
  const nextAllocations = allocations.map(a => {
    const next = editBySchedule.get(a.operatingRoomScheduleId);
    if (!next) return a;
    const prior = a.allocatedDoctorIds || [];
    if (sameIds(prior, next)) return a;          // edit is a no-op for this schedule
    touched = true;
    undo.push({ scheduleId: a.operatingRoomScheduleId, priorDoctorIds: [...prior] });
    return { ...a, allocatedDoctorIds: [...next] };
  });
  if (!touched) return null;

  const payload = nextAllocations.map(a => ({
    scheduleId: a.operatingRoomScheduleId,
    allocatedNurseIds: a.allocatedNurseIds || [],
    allocatedDoctorIds: a.allocatedDoctorIds || [],
    colorOverrides: a.colorOverrides || {},
  }));

  return { nextAllocations, payload, undo };
}

/** Convenience: turn "set these rooms' cases to one doctor" into per-schedule edits. */
export function editsForRooms(scheduleIds: string[], doctorId: string): DoctorEdit[] {
  return scheduleIds.map(scheduleId => ({ scheduleId, nextDoctorIds: [doctorId] }));
}

/** Convenience: turn a captured Undo entry back into edits that restore the prior doctor ids. */
export function editsFromUndo(changes: DoctorUndoChange[]): DoctorEdit[] {
  return changes.map(c => ({ scheduleId: c.scheduleId, nextDoctorIds: c.priorDoctorIds }));
}

// --- NURSE (CRNA) variant --------------------------------------------------
// CRNA moves/swaps must persist the same way (the CRNA-tab overlay `oc-nurse-transfers` never reached the
// allocation, so a hand-tuned CRNA in-time waterfall reverted on save). This mirrors the doctor builder but edits
// allocatedNurseIds. A nurse edit carries the FULL next nurse list for the schedule (CRNA + any students), because
// the caller preserves students per case while replacing the CRNA — unlike doctors, a schedule can hold >1 nurse.
export interface NurseEdit { scheduleId: string; nextNurseIds: string[]; }
export interface NurseUndoChange { scheduleId: string; priorNurseIds: string[]; }
export interface NurseMoveResult {
  nextAllocations: MovableAllocation[];
  payload: MovePayloadEntry[];
  undo: NurseUndoChange[];
}

export function buildNurseEditPayload(
  allocations: MovableAllocation[],
  edits: NurseEdit[],
  allocationsLoaded: boolean,
): NurseMoveResult | null {
  if (!allocationsLoaded) return null;
  if (!allocations.length) return null;

  const editBySchedule = new Map<string, string[]>();
  for (const e of edits) editBySchedule.set(e.scheduleId, e.nextNurseIds);

  const undo: NurseUndoChange[] = [];
  let touched = false;
  const nextAllocations = allocations.map(a => {
    const next = editBySchedule.get(a.operatingRoomScheduleId);
    if (!next) return a;
    const prior = a.allocatedNurseIds || [];
    if (sameIds(prior, next)) return a;
    touched = true;
    undo.push({ scheduleId: a.operatingRoomScheduleId, priorNurseIds: [...prior] });
    return { ...a, allocatedNurseIds: [...next] };
  });
  if (!touched) return null;

  const payload = nextAllocations.map(a => ({
    scheduleId: a.operatingRoomScheduleId,
    allocatedNurseIds: a.allocatedNurseIds || [],
    allocatedDoctorIds: a.allocatedDoctorIds || [],
    colorOverrides: a.colorOverrides || {},
  }));

  return { nextAllocations, payload, undo };
}

/** Turn a captured nurse Undo entry back into edits that restore the prior nurse ids. */
export function nurseEditsFromUndo(changes: NurseUndoChange[]): NurseEdit[] {
  return changes.map(c => ({ scheduleId: c.scheduleId, nextNurseIds: c.priorNurseIds }));
}
